import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Upload, Download, KeyRound, ShieldCheck, IdCard } from "lucide-react";
import CsvImportDialog from "../../components/CsvImportDialog";
import { exportCsv } from "../../lib/csvExport";

const EMPTY = { name:"", mobile:"", whatsapp:"", license_number:"", license_expiry:"", daily_allowance:500, salary:0, available:true };

const CSV_SCHEMA = [
  { key: "name",             header: "name",             type: "str", required: true },
  { key: "mobile",           header: "mobile",           type: "str", required: true },
  { key: "whatsapp",         header: "whatsapp",         type: "str" },
  { key: "license_number",   header: "license_number",   type: "str", required: true },
  { key: "license_expiry",   header: "license_expiry",   type: "str" },
  { key: "daily_allowance",  header: "daily_allowance",  type: "num", default: 500 },
  { key: "salary",           header: "salary",           type: "num", default: 0 },
  { key: "available",        header: "available",        type: "bool", default: true },
];
const CSV_SAMPLE = { name:"Ramesh Patil", mobile:"9890201047", whatsapp:"9890201047", license_number:"MH12-DL-999999", license_expiry:"2028-12-31", daily_allowance:500, salary:20000, available:true };

export default function AdminDrivers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);

  const load = () => api.get("/drivers").then(r=>setItems(r.data));
  useEffect(()=>{ load().catch(()=>{}); }, []);

  const save = async () => {
    try {
      const payload = {...form, daily_allowance: parseFloat(form.daily_allowance)||0, salary: parseFloat(form.salary)||0};
      if (editing) { await api.put(`/drivers/${editing}`, {...payload, id: editing}); toast.success("Saved"); }
      else {
        const r = await api.post("/drivers", payload);
        if (r.data?._generated_pin) toast.success(`Driver added. One-time PIN: ${r.data._generated_pin}`, { duration: 15000 });
        else toast.success("Driver added");
      }
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if(!window.confirm("Delete driver?")) return; await api.delete(`/drivers/${id}`); load(); toast.success("Deleted"); };
  const edit = (d) => { setEditing(d.id); setForm(d); setOpen(true); };

  const resetPin = async (d) => {
    if (!window.confirm(`Issue a new PIN for ${d.name}? Their old PIN will stop working.`)) return;
    try {
      const r = await api.post(`/drivers/${d.id}/reset-pin`, {});
      toast.success(`New PIN for ${d.name}: ${r.data.pin}`, { duration: 20000 });
      load();
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setPassword = async (d) => {
    const email = window.prompt(`Email for ${d.name}?`, d.email || "");
    if (!email) return;
    const inputPw = window.prompt(`New password for ${d.name}?\nLeave blank to auto-generate a strong one.`);
    // undefined = cancel, "" = auto-generate, string = use given
    if (inputPw === null) return;
    try {
      const body = inputPw ? { email, password: inputPw } : { email };
      const r = await api.post(`/drivers/${d.id}/set-password`, body);
      toast.success(`Email login enabled for ${d.name} — Email: ${r.data.email} · Password: ${r.data.password}`, { duration: 30000 });
      load();
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const viewLicense = async (d) => {
    try {
      const r = await api.get(`/drivers/${d.id}/license-photo`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load license photo");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-3xl font-display font-black tracking-tight">Drivers</h1>
        <div className="flex gap-2">
          <Button onClick={()=>exportCsv({filename:"drivers",headers:CSV_SCHEMA,rows:items})} variant="outline" data-testid="drv-export-csv-btn" disabled={items.length===0}><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
          <Button onClick={()=>setCsvOpen(true)} variant="outline" data-testid="drv-import-csv-btn"><Upload className="w-4 h-4 mr-1" /> Import CSV</Button>
          <Button onClick={()=>{setEditing(null);setForm(EMPTY);setOpen(true);}} className="bg-[#ea580c] hover:bg-orange-700" data-testid="add-driver-btn"><Plus className="w-4 h-4 mr-1" /> Add Driver</Button>
        </div>
      </div>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(d => (
          <div key={d.id} className="p-5 rounded-xl bg-white border border-slate-200" data-testid={`driver-item-${d.id}`}>
            <div className="font-display font-bold text-lg">{d.name}</div>
            <div className="text-sm text-slate-500">{d.mobile}</div>
            <div className="mt-2 text-xs text-slate-600">Lic: {d.license_number}</div>
            <div className="mt-2 text-xs">DA: ₹{d.daily_allowance} · {d.available?"Available":"Unavailable"}</div>
            <div className="mt-2 flex flex-wrap gap-1 text-[10px]">
              <span className={`px-2 py-0.5 rounded-full font-semibold ${d.has_pin ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{d.has_pin ? "PIN set" : "No PIN"}</span>
              <span className={`px-2 py-0.5 rounded-full font-semibold ${d.has_password ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{d.has_password ? "Password set" : "No password"}</span>
              {d.last_login_at && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Last: {new Date(d.last_login_at).toLocaleDateString()}</span>}
            </div>
            <div className="mt-4 flex gap-1.5 flex-wrap">
              <Button size="sm" variant="outline" onClick={()=>edit(d)} data-testid={`edit-driver-${d.id}`}><Edit className="w-3 h-3 mr-1" /> Edit</Button>
              <Button size="sm" variant="outline" onClick={()=>resetPin(d)} data-testid={`reset-pin-${d.id}`}><KeyRound className="w-3 h-3 mr-1 text-amber-600" /> Reset PIN</Button>
              <Button size="sm" variant="outline" onClick={()=>setPassword(d)} data-testid={`set-password-${d.id}`}><ShieldCheck className="w-3 h-3 mr-1 text-blue-600" /> Set Password</Button>
              {d.license_photo_path && (
                <Button size="sm" variant="outline" onClick={()=>viewLicense(d)} data-testid={`view-license-${d.id}`}><IdCard className="w-3 h-3 mr-1 text-emerald-600" /> View License</Button>
              )}
              <Button size="sm" variant="outline" onClick={()=>del(d.id)} data-testid={`delete-driver-${d.id}`}><Trash2 className="w-3 h-3 mr-1 text-red-500" /> Delete</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing?"Edit":"Add"} Driver</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="drv-name" /></div>
            <div><Label>Mobile</Label><Input value={form.mobile} onChange={e=>setForm({...form,mobile:e.target.value})} data-testid="drv-mobile" /></div>
            <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={e=>setForm({...form,whatsapp:e.target.value})} /></div>
            <div><Label>License Number</Label><Input value={form.license_number} onChange={e=>setForm({...form,license_number:e.target.value})} /></div>
            <div><Label>License Expiry</Label><Input type="date" value={form.license_expiry||""} onChange={e=>setForm({...form,license_expiry:e.target.value})} /></div>
            <div><Label>Daily Allowance ₹</Label><Input type="number" value={form.daily_allowance} onChange={e=>setForm({...form,daily_allowance:e.target.value})} /></div>
            <div><Label>Salary ₹</Label><Input type="number" value={form.salary} onChange={e=>setForm({...form,salary:e.target.value})} /></div>
            <div className="flex items-end gap-2"><Label>Available</Label><Switch checked={form.available} onCheckedChange={v=>setForm({...form,available:v})} /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-[#ea580c] hover:bg-orange-700" data-testid="drv-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <CsvImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        title="Import Drivers"
        endpoint="/drivers/bulk-import"
        schema={CSV_SCHEMA}
        sampleRow={CSV_SAMPLE}
        onImported={load}
      />
    </div>
  );
}
